import React, { useState } from "react";
import { useToast } from "@chakra-ui/react";
import { getCatalogBlocksAPI, updateCatalogBlockAPI } from "../../service/AuthService";
import "./general-lock.css";

const filterTree = (nodes, query) => {
  if (!query) return nodes;
  return nodes.reduce((matches, node) => {
    if (node.name.toLowerCase().includes(query)) matches.push(node);
    else {
      const children = filterTree(node.children || [], query);
      if (children.length) matches.push({ ...node, children });
    }
    return matches;
  }, []);
};

function CatalogNode({ node, nodeKey, onSelect, busy, openNodes, onToggle }) {
  const children = node.children || [];
  const hasChildren = children.length > 0;
  const blocked = node.blockedBy === "SELF" || node.blockedBy === "UPLINE";
  const reason = node.blockedBy === "UPLINE" ? "Blocked by upline"
    : node.blockedBy === "SELF" && !node.selfBlock ? "Unblock the parent first" : "";
  const open = openNodes[nodeKey] === true;

  return (
    <li className="general-lock-node">
      <div className="general-lock-node-row">
        {hasChildren ? (
          <button type="button" className="general-lock-expand"
            onClick={() => onToggle(nodeKey)}
            aria-label={`${open ? "Collapse" : "Expand"} ${node.name}`}>
            {open ? "⌄" : "›"}
          </button>
        ) : <span className="general-lock-expand" aria-hidden="true" />}
        <label className="general-lock-item" title={reason || node.name}>
          <input type="checkbox" checked={blocked} disabled={!node.canUpdate || busy}
            onChange={() => onSelect(node, !blocked)}
            aria-label={`${blocked ? "Unblock" : "Block"} ${node.name}`} />
          <span className="general-lock-checkbox" aria-hidden="true" />
          <span className="general-lock-item-name">{node.name}</span>
          {reason && <span className="general-lock-reason">{reason}</span>}
        </label>
      </div>
      {hasChildren && open && (
        <ul className="general-lock-children">
          {children.map((child) => (
            <CatalogNode key={`${child.sport}-${child.layer}-${child.id}`} node={child}
              nodeKey={`${nodeKey}/${child.sport}-${child.layer}-${child.id}`} onSelect={onSelect}
              busy={busy} openNodes={openNodes} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function GeneralLock() {
  const [search, setSearch] = useState("");
  const [loadPin, setLoadPin] = useState("");
  const [modalPin, setModalPin] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [openNodes, setOpenNodes] = useState({});
  const toast = useToast({ position: "top" });

  const notifyError = (response, fallback) => toast({
    title: "General Lock",
    description: typeof response?.response === "string" ? response.response : response?.message || fallback,
    status: "error", duration: 3500, isClosable: true,
  });

  const loadCatalog = async (event) => {
    event.preventDefault();
    if (!loadPin.trim()) {
      notifyError(null, "Transaction PIN is required to load the catalog.");
      return;
    }
    setLoading(true);
    const response = await getCatalogBlocksAPI(loadPin);
    setLoadPin("");
    setLoading(false);
    if (response?.code === 200 && Array.isArray(response.response)) {
      setCatalog(response.response);
      setOpenNodes({});
      setLoaded(true);
    } else notifyError(response, "Unable to load catalog blocks.");
  };

  const closeModal = () => {
    if (updating) return;
    setPending(null);
    setModalPin("");
  };

  const confirmToggle = async (event) => {
    event.preventDefault();
    if (!pending || updating) return;
    if (!modalPin.trim()) {
      notifyError(null, "Transaction PIN is required.");
      return;
    }
    setUpdating(true);
    const pin = modalPin;
    const response = await updateCatalogBlockAPI({
      sport: pending.node.sport, layer: pending.node.layer, targetId: pending.node.id,
      blocked: pending.blocked, transactionPassword: pin,
    });
    if (response?.code === 200) {
      const refreshed = await getCatalogBlocksAPI(pin);
      if (refreshed?.code === 200 && Array.isArray(refreshed.response)) {
        setCatalog(refreshed.response);
        setOpenNodes({});
        toast({ title: `${pending.node.name} ${pending.blocked ? "blocked" : "unblocked"}`,
          status: "success", duration: 2500, isClosable: true });
      } else {
        // A failed refresh must not leave a stale checkbox on screen.
        setCatalog([]);
        setLoaded(false);
        notifyError(refreshed, "Lock updated, but the catalog could not be refreshed. Load it again.");
      }
      setPending(null);
      setModalPin("");
    } else notifyError(response, "Unable to update catalog block.");
    setUpdating(false);
  };

  const reset = () => {
    setSearch(""); setLoadPin(""); setCatalog([]); setLoaded(false); setOpenNodes({});
  };
  const query = search.trim().toLowerCase();
  const events = filterTree(catalog.filter((node) => node.sport !== "CASINO"), query);
  const casinos = filterTree(catalog.filter((node) => node.sport === "CASINO"), query);
  const selectNode = (node, blocked) => setPending({ node, blocked });
  const toggleNode = (nodeKey) => setOpenNodes((current) => {
    const isOpen = current[nodeKey];
    const separatorIndex = nodeKey.lastIndexOf("/");
    const parentKey = separatorIndex === -1 ? null : nodeKey.slice(0, separatorIndex);
    const next = parentKey
      ? Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(`${parentKey}/`)))
      : {};
    return isOpen ? next : { ...next, [nodeKey]: true };
  });

  return (
    <main className="main general-lock-page">
      <div className="container-fluid"><div className="main-container">
        <section className="general-lock-panel">
          <h2>General Lock</h2>
          <form className="general-lock-form" onSubmit={loadCatalog}>
            <input type="search" placeholder="Search Events or Casino Games"
              aria-label="Search Events or Casino Games" value={search}
              onChange={(event) => setSearch(event.target.value)} />
            <input type="password" placeholder="Transaction PIN to load"
              aria-label="Transaction PIN to load catalog" value={loadPin}
              onChange={(event) => setLoadPin(event.target.value)} autoComplete="off" />
            <button type="submit" className="btn btn-dark" disabled={loading || updating}>
              {loading ? "Loading..." : "Load"}
            </button>
            <button type="button" className="btn btn-light" onClick={reset} disabled={loading || updating}>Reset</button>
          </form>
          {loaded && <div className="general-lock-columns">
            <section className="general-lock-column" aria-label="Events">
              <h3>Events</h3>
              {events.length ? <ul className="general-lock-list">
                {events.map((node) => <CatalogNode key={`${node.sport}-${node.layer}-${node.id}`}
                  node={node} nodeKey={`${node.sport}-${node.layer}-${node.id}`} onSelect={selectNode}
                  busy={updating} openNodes={openNodes} onToggle={toggleNode} />)}
              </ul> : <p className="general-lock-empty">No events found.</p>}
            </section>
            <section className="general-lock-column" aria-label="Casino list">
              <h3>Casino List</h3>
              {casinos.length ? <ul className="general-lock-list">
                {casinos.map((node) => <React.Fragment key={`${node.sport}-${node.layer}-${node.id}`}>
                  <CatalogNode node={{ ...node, children: [] }} nodeKey={`${node.sport}-${node.layer}-${node.id}`}
                    onSelect={selectNode} busy={updating} openNodes={openNodes} onToggle={toggleNode} />
                  {(node.children || []).map((game) => <CatalogNode
                    key={`${game.sport}-${game.layer}-${game.id}`} node={game}
                    nodeKey={`${node.sport}-${node.layer}-${node.id}/${game.sport}-${game.layer}-${game.id}`}
                    onSelect={selectNode} busy={updating} openNodes={openNodes} onToggle={toggleNode} />)}
                </React.Fragment>)}
              </ul> : <p className="general-lock-empty">No casino games found.</p>}
            </section>
          </div>}
        </section>
      </div></div>

      {pending && (
        <div className="modal fade show d-block general-lock-modal" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <form onSubmit={confirmToggle}>
                <header className="modal-header">
                  <h5 className="modal-title">General Lock</h5>
                  <button type="button" aria-label="Close" className="close" onClick={closeModal} disabled={updating}>×</button>
                </header>
                <div className="modal-body">
                  <div className="general-lock-modal-item">
                    <span className="general-lock-checkbox general-lock-checkbox--locked" aria-hidden="true">✓</span>
                    <span>{pending.node.name}</span>
                  </div>
                  <div className="row">
                    <div className="col-6" />
                    <div className="col-6">
                      <input className="form-control" type="password" placeholder="Transaction Code"
                        aria-label="Transaction Code" value={modalPin}
                        onChange={(event) => setModalPin(event.target.value)}
                        autoComplete="off" autoFocus disabled={updating} />
                    </div>
                  </div>
                </div>
                <footer className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeModal} disabled={updating}>Cancel</button>
                  <button type="submit" className="btn btn-dark" disabled={updating}>
                    {updating ? "Saving..." : pending.blocked ? "Block" : "Unblock"}
                  </button>
                </footer>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
